import { useRef, useCallback, useEffect, useState } from 'react';

export type SoundId = 'heartbeat' | 'gong' | 'beep' | 'applause';

interface AudioManagerOptions {
  baseUrl?: string;
}

/**
 * Centralized audio manager hook for QuizCrunch admin (host view)
 * Handles preloading, playback, and muting of game sound effects
 */
export const useAudioManager = (options: AudioManagerOptions = {}) => {
  const baseUrl = options.baseUrl || 'https://quizriot-prod-play-static-website-962595531541.s3.us-east-2.amazonaws.com/media';

  const audioRefs = useRef<Map<SoundId, HTMLAudioElement>>(new Map());
  const backgroundMusicRef = useRef<HTMLAudioElement | null>(null);
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [isMuted, setIsMuted] = useState(() => {
    const stored = localStorage.getItem('quizcrunch_admin_soundEnabled');
    return stored === 'false'; // Default to not muted
  });

  // Sound effect file mapping
  const soundFiles: Record<SoundId, string> = {
    heartbeat: `${baseUrl}/heartbeat.wav`,
    gong: `${baseUrl}/gong.wav`,
    beep: `${baseUrl}/beep.wav`,
    applause: `${baseUrl}/applause.wav`,
  };

  /**
   * Preload all sound effects
   */
  const preloadSounds = useCallback(() => {
    console.log('🔊 Preloading sound effects...');
    const sounds: SoundId[] = ['heartbeat', 'gong', 'beep', 'applause'];

    sounds.forEach((soundId) => {
      if (!audioRefs.current.has(soundId)) {
        const audio = new Audio(soundFiles[soundId]);
        audio.preload = 'auto';
        audio.volume = 1.0; // Set sound effects to 100% volume

        // Set loop for heartbeat
        if (soundId === 'heartbeat') {
          audio.loop = true;
        }

        audioRefs.current.set(soundId, audio);
      }
    });

    // Wait for all to be ready
    Promise.all(
      Array.from(audioRefs.current.values()).map(
        (audio) => new Promise<void>((resolve) => {
          if (audio.readyState >= 2) { // HAVE_CURRENT_DATA or better
            resolve();
          } else {
            audio.addEventListener('canplay', () => resolve(), { once: true });
            audio.addEventListener('error', () => {
              console.warn('Failed to load audio');
              resolve(); // Continue even if one fails
            }, { once: true });
          }
        })
      )
    ).then(() => {
      console.log('✅ All sound effects preloaded');
      setIsPreloaded(true);
    }).catch((err) => {
      console.warn('⚠️ Some sound effects failed to preload:', err);
      setIsPreloaded(true); // Continue anyway
    });
  }, [soundFiles]);

  /**
   * Play a sound effect
   */
  const playSound = useCallback((soundId: SoundId, volume?: number) => {
    if (isMuted) {
      console.log(`🔇 Sound muted: ${soundId}`);
      return;
    }

    const audio = audioRefs.current.get(soundId);
    if (!audio) {
      console.warn(`⚠️ Sound not found: ${soundId}`);
      return;
    }

    try {
      // Set volume if provided
      if (volume !== undefined) {
        audio.volume = Math.max(0, Math.min(1, volume));
      }

      // Reset to start if already playing (except for looping sounds)
      if (!audio.loop) {
        audio.currentTime = 0;
      }

      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log(`🔊 Playing: ${soundId}`);
          })
          .catch((error) => {
            console.warn(`⚠️ Audio play failed for ${soundId}:`, error);
          });
      }
    } catch (error) {
      console.warn(`⚠️ Error playing ${soundId}:`, error);
    }
  }, [isMuted]);

  /**
   * Stop a sound effect
   */
  const stopSound = useCallback((soundId: SoundId) => {
    const audio = audioRefs.current.get(soundId);
    if (!audio) return;

    try {
      audio.pause();
      audio.currentTime = 0;
      console.log(`⏸️ Stopped: ${soundId}`);
    } catch (error) {
      console.warn(`⚠️ Error stopping ${soundId}:`, error);
    }
  }, []);

  /**
   * Toggle mute state
   */
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      localStorage.setItem('quizcrunch_admin_soundEnabled', String(!newMuted));
      console.log(`🔊 Sound ${newMuted ? 'muted' : 'unmuted'}`);

      // Stop all currently playing sounds when muting
      if (newMuted) {
        audioRefs.current.forEach((audio) => {
          if (!audio.paused) {
            audio.pause();
            audio.currentTime = 0;
          }
        });
      }

      return newMuted;
    });
  }, []);

  /**
   * Set mute state directly
   */
  const setMute = useCallback((muted: boolean) => {
    setIsMuted(muted);
    localStorage.setItem('quizcrunch_admin_soundEnabled', String(!muted));

    // Stop all sounds when muting
    if (muted) {
      audioRefs.current.forEach((audio) => {
        if (!audio.paused) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    }
  }, []);

  /**
   * Register the background music audio element
   */
  const registerBackgroundMusic = useCallback((audioElement: HTMLAudioElement | null) => {
    backgroundMusicRef.current = audioElement;
    if (audioElement) {
      audioElement.volume = 0.5; // Set background music to 50% volume
      console.log(`🎵 Registered background music (volume: ${audioElement.volume})`);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioRefs.current.forEach((audio) => {
        audio.pause();
        audio.src = '';
      });
      audioRefs.current.clear();
      backgroundMusicRef.current = null;
    };
  }, []);

  return {
    playSound,
    stopSound,
    preloadSounds,
    toggleMute,
    setMute,
    isMuted,
    isPreloaded,
    registerBackgroundMusic,
  };
};
